/**
 * Magic-Link und Passwort-Zuruecksetzen: die Sitzung aus dem URL-Fragment uebernehmen.
 *
 * **Warum das von Hand sein muss.** GoTrue `/auth/v1/verify` antwortet mit einem
 * IMPLIZITEN Fragment (`#access_token=…&refresh_token=…`). Unser Browser-Client kann
 * das nicht selbst verwerten: `createBrowserClient` aus `@supabase/ssr` setzt
 * `flowType: "pkce"` fest (`createBrowserClient.js`, nach dem Spread der eigenen
 * Optionen — also nicht ueberschreibbar), und `GoTrueClient._getSessionFromURL` wirft
 * bei genau dieser Paarung sofort:
 *
 *     case 'implicit':
 *       if (this.flowType === 'pkce') throw AuthPKCEGrantCodeExchangeError('Not a valid PKCE flow url.')
 *
 * Der Wurf passiert VOR jedem Netzwerkaufruf, und `_initialize()` verschluckt ihn
 * (nur `_debug`). Nach aussen sah das aus wie „Sitzung kam einfach nicht an":
 * gemessen am 2026-08-11 endete jeder Magic-Link auf `?error=session_failed`, ohne
 * eine einzige Anfrage an `/auth/v1/*` nach dem Redirect.
 *
 * `setSession` umgeht die Sperre, ist aber kein Trick: es prueft das Token
 * serverseitig, bevor es eine Sitzung anlegt. Ein gefaelschtes Fragment wird
 * abgewiesen. Das Fragment selbst erreicht den Server nie (Browser senden es nicht).
 *
 * Der PKCE-Weg (`?code=` in der Query) bleibt unberuehrt — dafuer ist weiterhin
 * `exchangeCodeForSession` zustaendig. Diese Funktion fasst nur das Fragment an.
 */

/** Nur der Teil des Supabase-Clients, den wir hier brauchen — haelt den Test schlank. */
export interface SitzungsClient {
  auth: {
    setSession(t: { access_token: string; refresh_token: string }): Promise<{ error: { message: string } | null }>;
  };
}

export interface ImpliziteSitzung {
  uebernommen: boolean;
  /** `magiclink`, `recovery`, `invite`, … — entscheidet, wohin der Aufrufer weiterleitet. */
  typ: string | null;
  fehler: string | null;
}

export async function uebernimmSitzungAusHash(
  supabase: SitzungsClient,
  hash: string,
): Promise<ImpliziteSitzung> {
  const p = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const typ = p.get("type");
  const access_token = p.get("access_token");
  const refresh_token = p.get("refresh_token");

  // Beide oder keins: mit nur einem der Tokens legt `setSession` keine Sitzung an,
  // der Versuch wuerde nur eine irrefuehrende Fehlermeldung erzeugen.
  if (!access_token || !refresh_token) return { uebernommen: false, typ, fehler: null };

  try {
    const { error } = await supabase.auth.setSession({ access_token, refresh_token });
    return { uebernommen: !error, typ, fehler: error?.message ?? null };
  } catch (e) {
    return { uebernommen: false, typ, fehler: e instanceof Error ? e.message : String(e) };
  }
}
